import { createContext, useState, useEffect, ReactNode } from 'react';
import { api, setToken, removeToken, getToken } from '../api/client';

export interface User {
  id: string;
  email: string;
  name: string;
  nameKana?: string;
  department?: string;
  phone?: string;
  role: string;
  roleCode: string;
  permissions: string[];
  avatarUrl?: string;
}

interface LoginResponse extends User {
  token?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const userData = await api.get<User>('/api/auth/me');
      setUser(userData);
    } catch {
      removeToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await api.post<LoginResponse>('/api/auth/login', { email, password });
    
    // トークンがレスポンスに含まれている場合は保存
    if (response.token) {
      setToken(response.token);
    }
    
    setUser({
      id: response.id,
      email: response.email,
      name: response.name,
      nameKana: response.nameKana,
      role: response.role,
      roleCode: response.roleCode,
      permissions: Array.isArray(response.permissions) ? response.permissions : [],
      avatarUrl: response.avatarUrl,
    });
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout', {});
    } catch {
      // エラーでも続行
    }
    removeToken();
    setUser(null);
  };

  const updateUser = (userData: User) => {
    setUser(userData);
  };

  const refreshUser = async () => {
    try {
      const userData = await api.get<User>('/api/auth/me');
      setUser(userData);
    } catch {
      // Silently fail
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
